import { useCallback, useState } from "react";
import OrderCard from "../../components/OrderCard";
import { PageProps } from "../../components/types";
import useClaimOrder from "../../hooks/useClaimOrder";
import PageContainer from "../PageContainer";
import Modal from "../../components/Modal";

import { DgtPdProductionOrder } from "../../models";
import { formatNumberV2 } from "../../utilities/Format";
import { 
  Badge,
  SearchField,
  SelectField 
} from "@aws-amplify/ui-react";
import { FaSort } from "react-icons/fa";
import { getBrandLogoUrl } from "../../utilities/Image";
import DatePicker from 'react-datepicker'
import Select from 'react-select';
import { 
  useForm,
  SubmitHandler,
  Controller
} from 'react-hook-form'
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { FGInspectionTypes } from "../../constants/EnumType";

import { updateFGInspectionForm } from '../../apis/order'
import { IoIosCalendar } from "react-icons/io";
import { sub } from "date-fns";

export default function OrderClaim({ title, readOnly }: PageProps) {
  const [ openModal, setOpenModal ] = useState<boolean>(false)
  const [ selectedOrder, setSelectedOrder ] = useState<DgtPdProductionOrder>()
  const [ loading, setLoading ] = useState<boolean>(false)
  const [ keyword, setKeyword ] = useState<string>('')
  const [ sort, setSort ] = useState<string>('unselect')

  const treeDayAgo = sub(new Date(), { days: 3 })
  const [ dateRange, setDateRange ] = useState<[Date | null, Date | null]>([
    treeDayAgo, 
    new Date() // to is today
  ]);
  const [ startDate, endDate ] = dateRange;
  const [ claimOrders, mutate ] = useClaimOrder(startDate, endDate, keyword, sort)
  
  const schema = yup.object({
    forms: yup.array().min(1, 'กรุณาเลือกอย่างน้อย 1 Form')
  })
  const { 
    handleSubmit,
    formState: { errors },
    control,
    reset
  } = useForm<{ forms: string[] }>({
    defaultValues: {
      forms: []
    },
    resolver: yupResolver(schema)
  })
  
  const onEditForms = useCallback((order : DgtPdProductionOrder) => {
    if (order.fgInspectionTypes) {
      const selectedFGInspectionForms : string[] = JSON.parse(order.fgInspectionTypes)
      reset({ forms: selectedFGInspectionForms })
    }
    setSelectedOrder(order)
    setOpenModal(true)
  }, [ reset ])

  const onSubmitForm: SubmitHandler<{ forms: string[] }> = async (data) => {
    setLoading(true)
    const updated = await updateFGInspectionForm(
      selectedOrder?.id || '', data.forms)
    if (updated) {
      mutate(claimOrders.map(ord => ord.id === selectedOrder?.id ? updated : ord))
    }
    
    setOpenModal(false)
    setSelectedOrder(undefined)
    reset({ forms: []})
    setLoading(false)
  }

  return <PageContainer title={title} className="space-y-3 ">
    <div className="grid items-end grid-cols-2 gap-2 pb-3 md:grid-cols-3">
      <div className="relative">
        <span className='absolute inset-y-0 left-0 z-10 flex items-center px-3 text-muted'>
          <IoIosCalendar />
        </span>
        <DatePicker
          selectsRange={true}
          className='mr-5 pl-9 input-form-date py-[0.5rem]'
          onChange={dates => setDateRange(dates)}
          startDate={startDate}
          endDate={endDate}
          dateFormat="dd/MM/yyyy"
        />
      </div>
      <SelectField
        label=""
        icon={<FaSort />}
        labelHidden
        value={sort}
        onChange={(e) => setSort(e.target.value)}
      >
        <option value="unselect">เรียงตามที่ยังไม่เลือก Form</option>
        <option value="order">เรียงตาม ORDER</option>
        <option value="material">เรียงตาม MATERIAL</option>
        <option value="brand">เรียงตาม BRAND</option>
      </SelectField>
      <SearchField label="search" placeholder='ค้นหาด้วย PD Order...' onChange={v => setKeyword(v.currentTarget.value)} />
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {claimOrders.map(ord =>
        <OrderCard
          key={ord.id}
          productionOrder={ord}
          onEdit={onEditForms}
          readonly={readOnly || false}
        />
      )}
    </div>
    <Modal
      title="เลือก FORM"
      show={openModal}
      onHide={() => {
        setOpenModal(false)
        setSelectedOrder(undefined)
        reset({ forms: []})
      }}
    >
      {selectedOrder &&
      <div className="inline-flex flex-col items-start justify-start w-full gap-6">
        <div className="flex-col justify-start items-start gap-[9px] flex w-full">
          <div className="inline-flex items-start justify-between w-full gap-10">
            <h6 className="font-normal text-stone-500">#{selectedOrder.id}</h6>
            <img alt="" className=" w-[48px] h-[42px]" src={getBrandLogoUrl(selectedOrder.materialMrp || '')} />
          </div>
          <div className="flex flex-col items-start justify-start gap-2">
            <Badge variation="error">{selectedOrder.materialCode}</Badge>
            <h4 className="font-medium">{selectedOrder.materialDescription}</h4>
          </div>
          <div className="w-full items-start justify-between gap-2.5 inline-flex">
            <h6 className="font-normal text-stone-500">{selectedOrder.division}</h6>
            <h6 className="font-normal text-stone-500">{formatNumberV2(selectedOrder.orderQty)} {selectedOrder.materialUnit}</h6>
          </div>
        </div>
        <div className="w-full ">
          <form onSubmit={handleSubmit(onSubmitForm)}>
            <Controller
              name="forms"
              control={control}
              render={({ field }) =>
                <Select
                  isMulti
                  placeholder="เลือกอย่างน้อย 1 Form"
                  menuPortalTarget={document.body}
                  styles={{ 
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    option: (styles, { data }) => {
                      return {
                        ...styles,
                        color: data.value.startsWith('PD') ? 'green' : '#f87171',
                      }
                    },
                    multiValue: (styles, { data }) => {
                      return {
                        ...styles,
                        backgroundColor: data.value.startsWith('PD') ? 'rgba(0, 128, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)',
                      }
                    },
                    multiValueLabel: (styles, { data }) => ({
                      ...styles,
                      color: data.value.startsWith('PD') ? 'green' : '#f87171',
                    })
                  }}
                  options={Object.keys(FGInspectionTypes).map(key => ({ label: key, value: key }))}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={field.value.map((v: any) => ({ label: v, value: v })) || []}
                  onChange={vals => field.onChange(vals.map((v: any) => v.label))}
                />
              }
            />
            {errors && errors.forms ? <small className="error-label">กรุณาเลือกอย่างน้อย 1 Form</small> : null}
            <div className="flex gap-3 mt-6">
              <button disabled={loading} className='w-full btn-cancel' type="button" onClick={() => {
                setOpenModal(false)
                setSelectedOrder(undefined)
                reset({ forms: []})
              }} >ยกเลิก</button>
              <button disabled={loading} className='w-full gap-3 btn-order-claim' >ยืนยัน</button>
            </div>
          </form>
        </div>
      </div>}
    </Modal>
  </PageContainer>
}